import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Pagination,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  useTheme,
  Chip,
  Fab
} from '@mui/material';
import { Refresh, Download, FileDownload } from '@mui/icons-material';
import { useOrganizationActivities } from '../../hooks/useActivityLogs';
import { exportActivitiesCSV } from '../../network/apis/activity.api';
import { useAuth0 } from '@auth0/auth0-react';
import { useToast } from '../../hooks/useToast';
import ActivityItem from './ActivityItem';

/**
 * Activity categories for filter dropdown
 */
const ACTIVITY_CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'FLEET', label: 'Fleet' },
  { value: 'TEAM', label: 'Team' },
  { value: 'MISSION', label: 'Mission' },
  { value: 'LAANC', label: 'LAANC' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'FILES', label: 'Files' },
  { value: 'COMPLIANCE', label: 'Compliance' }
];

/**
 * ActivityFeed component - displays activity logs with filters and pagination
 */
const ActivityFeed = ({
  orgId: orgUuid,
  title = 'Activity Feed',
  showFilters = true,
  showPagination = true,
  compact = false,
  limit = 20,
  height = '600px',
  defaultCategory = ''
}) => {
  // Detect mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState(defaultCategory);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  // Infinite scroll state (mobile only)
  const [allActivities, setAllActivities] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);
  const observerTarget = useRef(null);

  // Pull-to-refresh state (mobile only)
  const [pullStartY, setPullStartY] = useState(0);
  const [pullCurrentY, setPullCurrentY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullThreshold = 80; // pixels to trigger refresh

  // Category menu anchor (mobile only)
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState(null);

  // Hooks
  const { getAccessTokenSilently } = useAuth0();
  const { showSuccess, showError } = useToast();

  // Calculate offset based on page (desktop pagination)
  const offset = isMobile ? 0 : (page - 1) * limit;

  // Fetch activities using the hook
  const { activities, total, loading, error, refetch } = useOrganizationActivities(
    orgUuid,
    {
      category: category || undefined,
      limit: isMobile ? limit : limit,
      offset: isMobile ? allActivities.length : offset
    },
    true
  );

  // Calculate total pages (desktop only)
  const totalPages = Math.ceil(total / limit);

  // Handle category filter change
  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setCategory(newCategory);
    setPage(1); // Reset to first page when filter changes
    // Reset infinite scroll
    if (isMobile) {
      setAllActivities([]);
      setHasMore(true);
    }
  };

  // Handle page change (desktop only)
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (isMobile) {
      setAllActivities([]);
      setHasMore(true);
      setIsRefreshing(true);
    }
    await refetch();
    if (isMobile) {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  // Pull-to-refresh handlers (mobile only)
  const handleTouchStart = (e) => {
    if (!isMobile || scrollContainerRef.current?.scrollTop > 0) return;
    setPullStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || scrollContainerRef.current?.scrollTop > 0) return;

    const currentY = e.touches[0].clientY;
    const pullDistance = currentY - pullStartY;

    if (pullDistance > 0) {
      setIsPulling(true);
      setPullCurrentY(Math.min(pullDistance, pullThreshold * 1.5));
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isPulling) return;

    const pullDistance = pullCurrentY;
    setIsPulling(false);
    setPullCurrentY(0);

    if (pullDistance >= pullThreshold && !isRefreshing) {
      handleRefresh();
    }
  };

  // Update activities for mobile infinite scroll
  useEffect(() => {
    if (isMobile && !loading && activities.length > 0) {
      if (allActivities.length === 0) {
        // First load
        setAllActivities(activities);
      } else {
        // Append new activities
        setAllActivities(prev => [...prev, ...activities]);
      }

      // Check if there are more activities to load
      setHasMore(allActivities.length + activities.length < total);
      setLoadingMore(false);
    }
  }, [activities, loading, isMobile]);

  // Reset when switching between mobile/desktop or category changes
  useEffect(() => {
    if (isMobile) {
      setAllActivities([]);
      setHasMore(true);
    }
  }, [category, isMobile]);

  // Intersection Observer for infinite scroll (mobile only)
  useEffect(() => {
    if (!isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loading && !loadingMore) {
          setLoadingMore(true);
          refetch();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [isMobile, hasMore, loading, loadingMore, refetch]);

  // Handle export dialog open
  const handleOpenExportDialog = () => {
    setExportDialogOpen(true);
  };

  // Handle export dialog close
  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  // Handle export
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = await getAccessTokenSilently();

      const params = {
        startDate: exportDateRange.startDate,
        endDate: exportDateRange.endDate,
        category: category || undefined
      };

      const { blob, filename } = await exportActivitiesCSV(token, orgUuid, params);

      // Create download link with backend-generated filename
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // Uses filename from Content-Disposition header
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Activity logs exported successfully');
      setExportDialogOpen(false);
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to export activity logs');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        height: height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 }
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          {title}
        </Typography>

        {/* Desktop Controls */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center'
            }}
          >
            {/* Category Filter */}
            {showFilters && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={handleCategoryChange}
                >
                  {ACTIVITY_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Refresh Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>

            {/* Export Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<Download />}
              onClick={handleOpenExportDialog}
              disabled={loading}
            >
              Export CSV
            </Button>
          </Box>
        )}

        {/* Mobile Category Chips */}
        {isMobile && showFilters && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              overflowX: 'auto',
              width: '100%',
              pb: 0.5,
              '&::-webkit-scrollbar': {
                height: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: '4px'
              }
            }}
          >
            {ACTIVITY_CATEGORIES.map((cat) => (
              <Chip
                key={cat.value}
                label={cat.label}
                onClick={() => {
                  setCategory(cat.value);
                  setAllActivities([]);
                  setHasMore(true);
                }}
                color={category === cat.value ? 'primary' : 'default'}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  height: '28px',
                  flexShrink: 0,
                  borderRadius: '6px'
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Activity List */}
      <Box
        ref={scrollContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: '#fafafa',
          position: 'relative'
        }}
      >
        {/* Pull-to-refresh indicator (mobile only) */}
        {isMobile && (isPulling || isRefreshing) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: Math.min(pullCurrentY, pullThreshold),
              backgroundColor: '#f5f5f5',
              transition: isPulling ? 'none' : 'height 0.3s ease',
              zIndex: 1
            }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} />
            ) : (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  opacity: pullCurrentY / pullThreshold,
                  fontSize: '0.7rem'
                }}
              >
                {pullCurrentY >= pullThreshold ? 'Release to refresh' : 'Pull to refresh'}
              </Typography>
            )}
          </Box>
        )}
        {/* Loading State (initial load) */}
        {loading && (isMobile ? allActivities.length === 0 : true) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Box sx={{ padding: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Empty State */}
        {!loading && !error && (isMobile ? allActivities.length === 0 : activities.length === 0) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography variant="body1" color="textSecondary">
              No activities found
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {category
                ? 'Try changing the filter or check back later'
                : 'Activities will appear here once actions are performed'}
            </Typography>
          </Box>
        )}

        {/* Activities List */}
        {!loading && !error && (isMobile ? allActivities.length > 0 : activities.length > 0) && (
          <Box>
            {(isMobile ? allActivities : activities).map((activity, index) => (
              <ActivityItem
                key={activity.id || `${activity.id}-${index}`}
                activity={activity}
                showCategory={!category}
                compact={compact}
              />
            ))}

            {/* Infinite scroll loading indicator (mobile only) */}
            {isMobile && hasMore && (
              <Box
                ref={observerTarget}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 2
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {/* End of list indicator (mobile only) */}
            {isMobile && !hasMore && allActivities.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 2
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  No more activities
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Pagination (desktop only) */}
      {!isMobile && showPagination && totalPages > 1 && !loading && (
        <>
          <Divider />
          <Box
            sx={{
              padding: { xs: 1.5, sm: 2 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              siblingCount={0}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: '28px', sm: '32px' },
                  height: { xs: '28px', sm: '32px' }
                }
              }}
            />
          </Box>
        </>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Export Activity Logs to CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Select a date range to export activity logs. The current category filter will be applied.
            </Typography>

            <TextField
              label="Start Date"
              type="date"
              value={exportDateRange.startDate}
              onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              label="End Date"
              type="date"
              value={exportDateRange.endDate}
              onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            {category && (
              <Alert severity="info">
                Export will include only "{ACTIVITY_CATEGORIES.find(c => c.value === category)?.label}" category activities.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} disabled={exportLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={exportLoading || !exportDateRange.startDate || !exportDateRange.endDate}
            startIcon={exportLoading ? <CircularProgress size={20} /> : <Download />}
          >
            {exportLoading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile FAB for Export (mobile only) */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="export"
          onClick={handleOpenExportDialog}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
        >
          <FileDownload />
        </Fab>
      )}
    </Paper>
  );
};

export default ActivityFeed;
